import { useState } from "react";
import axios from "axios";
import { motion } from "framer-motion";
import { toast } from "sonner";
import { Loader2, ShieldCheck } from "lucide-react";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const empty = { name: "", phone: "", address: "", roof_details: "" };

export default function QuoteForm() {
  const [form, setForm] = useState(empty);
  const [loading, setLoading] = useState(false);

  const update = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.phone || !form.address) {
      toast.error("Please fill in your name, phone, and address.");
      return;
    }
    setLoading(true);
    try {
      await axios.post(`${API}/quote`, form);
      toast.success("Request received! We'll reach out to schedule your free inspection.");
      setForm(empty);
    } catch (err) {
      toast.error("Something went wrong. Please call us at 443-250-9046.");
    } finally {
      setLoading(false);
    }
  };

  const inputCls =
    "bg-white/5 border-white/15 text-white placeholder:text-white/30 h-12 rounded-md focus-visible:ring-hero-red focus-visible:border-hero-red";

  return (
    <section id="quote" data-testid="quote-section" className="relative py-28 md:py-36 bg-hero-surface border-t border-white/10">
      <div className="max-w-7xl mx-auto px-6 grid lg:grid-cols-2 gap-16 items-start">
        <motion.div
          initial={{ opacity: 0, y: 40 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-80px" }}
          transition={{ duration: 0.7 }}
        >
          <p className="text-xs uppercase tracking-[0.3em] text-hero-red mb-4">— Free Inspection</p>
          <h2 className="font-display uppercase text-4xl sm:text-5xl lg:text-6xl leading-[0.95] mb-6">
            Book your free<br />roof inspection.
          </h2>
          <p className="text-hero-silver max-w-md text-lg">
            No cost, no obligation. Tell us about your roof and we&apos;ll come take a look. If insurance covers it, you pay only your deductible.
          </p>
          <div className="mt-10 flex items-center gap-3 text-hero-silver">
            <ShieldCheck className="h-5 w-5 text-hero-red" />
            <span className="text-sm">Only pay the deductible — we&apos;ll work through it with you every step of the way.</span>
          </div>
        </motion.div>

        <motion.form
          onSubmit={submit}
          data-testid="quote-form"
          initial={{ opacity: 0, y: 40 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-80px" }}
          transition={{ duration: 0.7, delay: 0.1 }}
          className="bg-hero-void border border-white/10 rounded-md p-8 space-y-5"
        >
          <div className="space-y-2">
            <Label htmlFor="name" className="text-white/80">Full Name</Label>
            <Input id="name" data-testid="quote-name" value={form.name} onChange={update("name")} placeholder="John Smith" className={inputCls} />
          </div>
          <div className="space-y-2">
            <Label htmlFor="phone" className="text-white/80">Phone Number</Label>
            <Input id="phone" data-testid="quote-phone" value={form.phone} onChange={update("phone")} placeholder="(443) 250-9046" className={inputCls} />
          </div>
          <div className="space-y-2">
            <Label htmlFor="address" className="text-white/80">Property Address</Label>
            <Input id="address" data-testid="quote-address" value={form.address} onChange={update("address")} placeholder="123 Main St, Catonsville, MD" className={inputCls} />
          </div>
          <div className="space-y-2">
            <Label htmlFor="roof" className="text-white/80">Tell us about your roof</Label>
            <Textarea id="roof" data-testid="quote-details" value={form.roof_details} onChange={update("roof_details")} placeholder="Storm damage, missing shingles, age of roof..." rows={4} className={inputCls + " h-auto py-3"} />
          </div>
          <button
            type="submit"
            disabled={loading}
            data-testid="quote-submit"
            className="w-full inline-flex items-center justify-center gap-2 bg-hero-red hover:bg-hero-reddark disabled:opacity-60 text-white font-bold uppercase tracking-wide text-sm py-4 rounded-md transition-colors duration-200"
          >
            {loading ? <><Loader2 className="h-4 w-4 animate-spin" /> Sending...</> : "Request Free Inspection"}
          </button>
        </motion.form>
      </div>
    </section>
  );
}
